import React, { useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { ArrowLeft, Lock, User, X, Mail } from "lucide-react";

import { useAuth } from "../context/AuthContext";

import { formatApiError, api } from "../lib/api";

import { toast } from "sonner";
import SocialAuthButtons, {
  SOCIAL_AUTH_ENABLED,
} from "../components/auth/SocialAuthButtons";
import logo from "../assets/accesswealth logo.png";
import logo2 from "../assets/accesswealth logo light.png";
import { useTheme } from "@/context/ThemeContext";

export default function Login() {
  const { login } = useAuth();
  const { theme } = useTheme();

  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState("");

  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [showForgotModal, setShowForgotModal] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");

  const [forgotLoading, setForgotLoading] = useState(false);

  const [forgotSuccess, setForgotSuccess] = useState("");

  // ==================================================
  // SUBMIT
  // ==================================================

  const onSubmit = async (e) => {
    e.preventDefault();

    setError("");

    setLoading(true);

    try {
      const u = await login({
        identifier: identifier.trim(),
        password,
      });

      toast.success("Welcome back");

      navigate(u?.role === "pfa" ? "/advisor" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* BACK */}

        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-6"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to home
        </Link>

        {/* CARD */}

        <div className="card-soft p-8">
          {/* HEADER */}

          <div className="flex items-center gap-3 mb-6">
            <div className="grid h-12 w-10 place-items-center  overflow-hidden ">
              {theme == "dark" ? (
                <>
                  <img
                    src={logo2}
                    alt="AccessWealth"
                    className="h-full w-full object-cover"
                  />
                </>
              ) : (
                <>
                  <img
                    src={logo}
                    alt="AccessWealth"
                    className="h-full w-full object-cover"
                  />
                </>
              )}
            </div>

            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                Welcome back
              </h1>
            </div>
          </div>

          {/* FORM */}

          <form onSubmit={onSubmit} className="space-y-4">
            {/* IDENTIFIER */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Email / Username / Phone
              </label>

              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                <input
                  type="text"
                  required
                  data-testid="login-identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="input-soft !pl-10"
                  placeholder="Enter email, username or phone"
                />
              </div>
            </div>

            {/* PASSWORD */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Password
              </label>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                <input
                  type="password"
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-soft !pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Forgot password?
              </button>
            </div>

            {/* ERROR */}

            {error && (
              <div
                data-testid="login-error"
                className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs p-3"
              >
                {error}
              </div>
            )}

            {/* SUBMIT */}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {/* SOCIAL LOGIN */}

          {SOCIAL_AUTH_ENABLED && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />

                <span className="text-[10px] uppercase tracking-widest text-slate-400">
                  or continue with
                </span>

                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>

              <SocialAuthButtons />
            </>
          )}

          {/* FOOTER */}

          <p className="mt-6 text-xs text-center text-slate-500">
            New to AccessWealth?{" "}
            <Link
              to="/register"
              data-testid="login-to-register"
              className="text-emerald-600 font-semibold"
            >
              Create an account
            </Link>
          </p>

        </div>
      </div>

      {showForgotModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
            {/* HEADER */}

            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                  Reset password
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Enter your email to receive a reset link.
                </p>
              </div>

              <button
                onClick={() => {
                  setShowForgotModal(false);
                  setForgotSuccess("");
                  setForgotEmail("");
                }}
                className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* BODY */}

            <div className="p-6">
              {forgotSuccess ? (
                <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-4">
                  <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    Email sent
                  </div>

                  <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                    If this email is registered, a password reset link has been
                    sent.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();

                    setForgotLoading(true);

                    try {
                      const res = await api.post("/auth/forgot-password", {
                        email: forgotEmail.trim(),
                      });

                      setForgotSuccess(res.data.message);

                      toast.success("Password reset email sent");
                    } catch (err) {
                      toast.error(formatApiError(err));
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Email address
                    </label>

                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                      <input
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="input-soft !pl-10"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="btn-primary w-full"
                  >
                    {forgotLoading ? "Sending..." : "Send reset link"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
